import React, { memo, useMemo, useState, useRef, useEffect } from 'react'
import { useTable, useSortBy, useFilters, useExpanded } from 'react-table'
import Table from 'react-bootstrap/Table'
import Dropdown from 'react-bootstrap/Dropdown'
import { matchSorter } from 'match-sorter'
import Form from 'react-bootstrap/Form'
import RowOfMessageInteractionIcons from '../../gateway/RowOfMessageInteractionIcons.jsx';
import IconButton from './IconButton.jsx';
import { buildSearchMatcher, SEARCH_SYNTAX_HELP } from '../textSearch.js';

const no_scroll_style = {
  position: 'sticky',
  top: '-1px',
  background: 'white',
  whiteSpace: 'noWrap',
  verticalAlign: 'text-top',
  zIndex: 2 // keep header cells above scrolling body cells
}

function DefaultColumnFilter({ column: { filterValue, preFilteredRows, setFilter } }) {
  const count = preFilteredRows.length
  return (
    <Form.Control
      className="styled-text-input"
      value={filterValue || ''}
      width={'100%'}
      size="sm"
      title={SEARCH_SYNTAX_HELP}
      onChange={e => {
        setFilter(e.target.value || undefined)
      }}
      placeholder={`Search ${count} ...`} />
  )
}

// Custom filter function for multi-select: keeps rows whose value is in the selected array
export function multiSelectFilter(rows, id, filterValue) {
  if (!filterValue || filterValue.length === 0) return rows
  return rows.filter(row => filterValue.includes(row.values[id]))
}
multiSelectFilter.autoRemove = val => !val || val.length === 0

// This is a custom filter UI for selecting
// multiple options from a list using checkboxes
export function SelectColumnFilter({column: {filterValue, setFilter, preFilteredRows, id}}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach(row => {
      const value = row.values[id]
      if (value !== null && value !== undefined) {
        options.add(value)
      }
    })
    return [...options.values()].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
    )
  }, [id, preFilteredRows])

  const selectedValues = filterValue || []

  const handleToggle = (option) => {
    const newSelected = selectedValues.includes(option)
      ? selectedValues.filter(v => v !== option)
      : [...selectedValues, option]
    setFilter(newSelected.length > 0 ? newSelected : undefined)
  }

  const label = selectedValues.length === 0
    ? 'All'
    : selectedValues.length === 1
      ? selectedValues[0]
      : `${selectedValues.length} selected`

  // Controlled `show` state lets us elevate the enclosing sticky <th>'s
  // stacking context while the menu is open, so the menu paints above
  // sibling header cells without any manual DOM re-parenting of the
  // React-managed menu node.
  const [show, setShow] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    const th = wrapperRef.current && wrapperRef.current.closest('th')
    if (!th) return undefined
    if (!show) return undefined
    // Save and bump z-index just above sibling sticky header cells
    // (which default to z-index: 2 via `no_scroll_style`). We
    // deliberately stay well below Bootstrap's modal/backdrop range
    // (1050+) so an open filter menu can never obscure a modal.
    const prev = th.style.zIndex
    th.style.zIndex = '3'
    return () => {
      // Restore on close *and* on unmount-while-open, so the header
      // cell never gets stuck at the elevated z-index.
      th.style.zIndex = prev
    }
  }, [show])

  // Render a multi-select dropdown with checkboxes
  return (
    <div ref={wrapperRef}>
    <Dropdown autoClose="outside" show={show} onToggle={(nextShow) => setShow(nextShow)}>
      <Dropdown.Toggle
        className="styled-form-select-input multi-select-toggle"
        size="sm"
        variant="light">
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="multi-select-menu"
        renderOnMount
        popperConfig={{
          strategy: 'fixed',
          // Anchor the right edge of the menu to the right edge of the
          // toggle so that, when the menu grows wider than the toggle to
          // fit long option labels, it expands to the left instead of
          // overflowing to the right of the column.
          placement: 'bottom-end',
          modifiers: [{
            // Make the menu at least as wide as the toggle. With
            // strategy:'fixed' CSS `min-width: 100%` resolves against the
            // viewport, so we copy the toggle's pixel width onto the
            // popper as min-width here. Content wider than the toggle
            // (long option labels) is still allowed to expand the menu.
            name: 'minWidthFromToggle',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({state}) => {
              state.styles.popper.minWidth = `${state.rects.reference.width}px`
            },
            effect: ({state}) => {
              state.elements.popper.style.minWidth = `${state.elements.reference.offsetWidth}px`
            },
          }, {
            // Cap the menu's max-height to the available viewport space
            // below (or above, when flipped) the toggle. The menu only
            // scrolls when it would otherwise overflow the viewport.
            name: 'viewportMaxHeight',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({state}) => {
              const ref = state.rects.reference
              const placement = state.placement || 'bottom-end'
              const viewportHeight = window.innerHeight
              const margin = 8 // small gap from viewport edge
              const available = placement.startsWith('top')
                ? ref.y - margin
                : viewportHeight - (ref.y + ref.height) - margin
              state.styles.popper.maxHeight = `${Math.max(80, available)}px`
              state.styles.popper.overflowY = 'auto'
            },
          }],
        }}>
        <Dropdown.Item as="div" onClick={() => setFilter(undefined)}>
          <Form.Check
            type="checkbox"
            checked={selectedValues.length === 0}
            label="All"
            readOnly
          />
        </Dropdown.Item>
        <Dropdown.Divider />
        {options.map((option) => (
          <Dropdown.Item as="div" key={String(option)} onClick={() => handleToggle(option)}>
            <Form.Check
              type="checkbox"
              checked={selectedValues.includes(option)}
              label={option}
              readOnly
            />
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
    </div>
  )
}


function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

fuzzyTextFilterFn.autoRemove = val => !val

export default memo(function ExpandableTable({
                                               columns,
                                               data,
                                               onToggleExpand,
                                               expandedRowIds,
                                               renderRowSubComponent,
                                               showColumn
                                             }) {
  const filterTypes = useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
      text: (rows, id, filterValue) => {
        // Supports wildcards (* and ?) and '|' as an OR operator.
        // Matches anywhere within the cell value (case-insensitive substring).
        const matcher = buildSearchMatcher(filterValue)
        return rows.filter(row => {
          const rowValue = row.values[id]
          return matcher(rowValue == null ? '' : String(rowValue))
        })
      }
    }),
    []
  )

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      // Use our custom 'text' filter (wildcards + '|' OR) for all text columns
      // unless a column explicitly opts into a different filter.
      filter: 'text'
    }),
    []
  )

  // We no longer add a separate show column; instead, we optionally
  // render the show action icon alongside the expand icon in the first cell
  const enhancedColumns = useMemo(() => columns, [columns])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable(
    {
      columns: enhancedColumns,
      data,
      defaultColumn,
      filterTypes
      // initial expanded state handled externally via expandedRowIds Set of transaction_keys
      // react-table's internal expanded state isn't used here
    },
    useFilters,
    useSortBy,
    useExpanded
  )

  const [copiedRowId, setCopiedRowId] = React.useState(null)
  const [previewRowId, setPreviewRowId] = useState(null)
  const [previewText, setPreviewText] = useState('')
  const previewTimerRef = useRef(null)

  const showOnClick = showColumn && typeof showColumn.onClick === 'function' ? showColumn.onClick : null
  const showOnDownload = showColumn && typeof showColumn.onDownload === 'function' ? showColumn.onDownload : null
  const showOnCopy = showColumn && typeof showColumn.onCopy === 'function' ? showColumn.onCopy : null
  const getPreview = showColumn && typeof showColumn.getPreview === 'function' ? showColumn.getPreview : null
  const hasCachedBody = showColumn && typeof showColumn.hasCachedBody === 'function' ? showColumn.hasCachedBody : null

  const handleCopyClick = async (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    if (showOnCopy) {
      await showOnCopy(row)
      setCopiedRowId(row.id)
      setTimeout(() => {
        setCopiedRowId((current) => (current === row.id ? null : current))
      }, 2000)
    }
  }

  const handlePreviewEnter = (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    if (!getPreview) return

    const rowId = row.id

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }

    setPreviewRowId(rowId)

    // If we already have the body cached for this row, get and display the preview immediately.
    if (hasCachedBody && hasCachedBody(row)) {
      (async () => {
        try {
          const text = await getPreview(row)
          setPreviewText(text || '')
        } catch (err) {
          setPreviewText('Error loading preview')
        }
      })()
      return
    }

    // Otherwise, we're not cached yet: show 'Loading preview…' immediately,
    // then start a debounce before actually fetching/parsing.
    setPreviewText('Loading preview…')

    previewTimerRef.current = setTimeout(async () => {
      try {
        const text = await getPreview(row)
        setPreviewText(text || '')
      } catch (err) {
        setPreviewText('Error loading preview')
      } finally {
        previewTimerRef.current = null
      }
    }, 600)
  }

  const handlePreviewLeave = (e, row) => {
    e.preventDefault()
    e.stopPropagation()
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    setPreviewRowId((current) => (current === row.id ? null : current))
    setPreviewText('')
  }

  return (
    <Table striped bordered hover size="sm" {...getTableProps()}>
      <thead>
      {headerGroups.map(headerGroup => {
        const headerGroupProps = headerGroup.getHeaderGroupProps()
        const { key: headerGroupKey, ...headerGroupRest } = headerGroupProps
        return (
          <tr key={headerGroupKey} style={{ verticalAlign: 'top' }} {...headerGroupRest}>
            <th style={no_scroll_style} width="100px">Message Content</th>
            {headerGroup.headers.map(column => {
              const headerProps = column.getHeaderProps()
              const { key: headerKey, ...headerRest } = headerProps
              return (
                <th key={headerKey} style={no_scroll_style} width={column.width} {...headerRest}>
                  <div>
                    {!column.canSort && column.render('Header')}
                    {column.canSort && (
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center', // center icon vertically with text
                          gap: '0.25rem',
                        }}{...column.getSortByToggleProps()}
                      >
                        <span>{column.render('Header')}</span>
                        <IconButton
                          iconName={column.isSorted ? (column.isSortedDesc ? 'sort down' : 'sort up') : 'sort'}
                          ariaLabel={column.isSorted ? (column.isSortedDesc ? 'No sort' : 'Sort descending') : 'Sort ascending'}
                          buttonStyle={{ marginBottom: 2, }}
                        />
                      </span>
                    )}
                    <br />
                  </div>
                  <div>{column.canFilter ? column.render('Filter') : <Form.Control readOnly size="sm" disabled />}</div>
                </th>
              )
            })}
          </tr>
        )
      })}
      </thead>
      <tbody {...getTableBodyProps()}>
      {rows.map((row) => {
        prepareRow(row)
        const rowProps = row.getRowProps()
        const { key: rowKey, ...rowRest } = rowProps
        const transactionKey = row.original?.transaction_key
        const isExpanded = expandedRowIds instanceof Set ? expandedRowIds.has(transactionKey) : false
        const isRowJustCopied = copiedRowId === row.id
        const isPreviewing = previewRowId === row.id

        return (
          <React.Fragment key={row.id}>
            <tr key={rowKey} {...rowRest}>
              <RowOfMessageInteractionIcons
                row={row}
                transactionKey={transactionKey}
                isExpanded={isExpanded}
                onToggleExpand={onToggleExpand}
                showOnClick={showOnClick}
                showOnDownload={showOnDownload}
                showOnCopy={showOnCopy}
                getPreview={getPreview}
                handleCopyClick={handleCopyClick}
                handlePreviewEnter={handlePreviewEnter}
                handlePreviewLeave={handlePreviewLeave}
                isRowJustCopied={isRowJustCopied}
                isPreviewing={isPreviewing}
                previewText={previewText}
              />
              {row.cells.map(cell => {
                const cellProps = cell.getCellProps()
                const { key: cellKey, ...cellRest } = cellProps
                return <td key={cellKey} {...cellRest}>{cell.render('Cell')}</td>
              })}
            </tr>
            {isExpanded && (
              <tr>
                <td colSpan={headerGroups[0]?.headers?.length + 1} style={{ background: '#f9f9f9' }}>
                  {renderRowSubComponent({ row })}
                </td>
              </tr>
            )}
          </React.Fragment>
        )
      })}
      </tbody>
    </Table>
  )
})
